export function formatCentsToDollars(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatCentsWithCurrency(
  cents: number,
  currency: string,
): string {
  return `$${formatCentsToDollars(cents)} ${currency}`;
}

export function parseDollarsToCents(value: string): number | null {
  const cleaned = value.replace(/[,$\s]/g, "");
  if (cleaned === "" || cleaned === "-") return null;
  const parsed = parseFloat(cleaned);
  if (Number.isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

/**
 * A decimal parsed into an exact integer and the power of ten it is scaled by,
 * so arithmetic never goes through float64.
 */
interface ScaledDecimal {
  units: bigint;
  scale: number;
}

const CONVERTED_PRICE_DECIMAL_PLACES = 2;

// The scale the Bank of Canada publishes at, in both directions.
const RATE_DISPLAY_DECIMAL_PLACES = 4;

function parseScaledDecimal(value: string): ScaledDecimal | null {
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }
  const isNegative = trimmed.startsWith("-");
  const unsigned = isNegative ? trimmed.slice(1) : trimmed;
  const [wholePart, fractionPart = ""] = unsigned.split(".");
  const units = BigInt(wholePart + fractionPart);
  return { units: isNegative ? -units : units, scale: fractionPart.length };
}

/**
 * Rescales to `decimalPlaces`, rounding half away from zero on the integer.
 *
 * Half-up on the exact product is what makes a figure shown here agree with the
 * same figure converted server-side by a Python Decimal: float64 holds some
 * exact ties fractionally below the midpoint, so `toFixed` would round them the
 * other way.
 */
function formatScaledDecimal(
  scaledDecimal: ScaledDecimal,
  decimalPlaces: number,
): string {
  const { units, scale } = scaledDecimal;
  const isNegative = units < 0n;
  const magnitude = isNegative ? -units : units;

  let rescaled: bigint;
  if (scale <= decimalPlaces) {
    rescaled = magnitude * 10n ** BigInt(decimalPlaces - scale);
  } else {
    const divisor = 10n ** BigInt(scale - decimalPlaces);
    const quotient = magnitude / divisor;
    const remainder = magnitude % divisor;
    rescaled = remainder * 2n >= divisor ? quotient + 1n : quotient;
  }

  const digits = rescaled.toString().padStart(decimalPlaces + 1, "0");
  const wholePart = digits.slice(0, digits.length - decimalPlaces);
  const fractionPart = digits.slice(digits.length - decimalPlaces);
  const sign = isNegative && rescaled !== 0n ? "-" : "";
  return decimalPlaces === 0
    ? `${sign}${wholePart}`
    : `${sign}${wholePart}.${fractionPart}`;
}

/**
 * Converts `amount` at `rate`, exactly.
 *
 * The server resolves direction, so this only ever multiplies. Returns null for
 * an amount or rate it cannot parse, which the caller renders as no conversion
 * rather than as an error beside a price that is itself correct.
 */
export function convertAmount(
  amount: string | number,
  rate: string,
): string | null {
  const parsedAmount = parseScaledDecimal(String(amount));
  const parsedRate = parseScaledDecimal(rate);
  if (parsedAmount === null || parsedRate === null) {
    return null;
  }
  return formatScaledDecimal(
    {
      units: parsedAmount.units * parsedRate.units,
      scale: parsedAmount.scale + parsedRate.scale,
    },
    CONVERTED_PRICE_DECIMAL_PLACES,
  );
}

function groupThousands(wholePart: string): string {
  return wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Renders an already-converted amount the way prices read elsewhere in the app.
 * Grouping is done on the string so a large amount cannot be perturbed by a
 * float round-trip.
 */
export function formatConvertedPrice(
  amount: string,
  currencyCode: string,
): string {
  const isNegative = amount.startsWith("-");
  const unsigned = isNegative ? amount.slice(1) : amount;
  const [wholePart, fractionPart = ""] = unsigned.split(".");
  const grouped = groupThousands(wholePart);
  const sign = isNegative ? "-" : "";
  return fractionPart === ""
    ? `${sign}$${grouped} ${currencyCode}`
    : `${sign}$${grouped}.${fractionPart} ${currencyCode}`;
}

/**
 * Renders a rate for display, at the scale the source itself publishes.
 *
 * The Bank of Canada publishes both directions to four decimal places, so that
 * is what a reader would see if they went and looked the rate up. The registry
 * stores and serializes at eight: a published rate arrives padded
 * (`1.38660000`), and an inverted one carries eight real digits
 * (`0.72118852`). Neither belongs in a sentence that calls the figure
 * approximate.
 *
 * Rounding here is display-only and deliberately does not feed the conversion,
 * which keeps using the full-precision rate — so a reader multiplying the
 * rounded rate by hand can land a cent or so away from the converted amount on
 * a large figure. That is the cost of a legible rate, and the reason the
 * tooltip says "approximate" rather than quoting a figure to the cent.
 *
 * Reuses the same exact-integer rescale as the conversion, so the rate is never
 * routed through a float on its way to being read.
 */
export function formatExchangeRate(rate: string): string {
  const parsedRate = parseScaledDecimal(rate);
  if (parsedRate === null) {
    return rate;
  }
  return formatScaledDecimal(parsedRate, RATE_DISPLAY_DECIMAL_PLACES)
    .replace(/0+$/, "")
    .replace(/\.$/, "");
}
